import { Fragment, type ReactNode } from "react";
import type { GraphDriveItem } from "../../email/services/graph";
import type { BreadcrumbEntry } from "../onedrive/hooks/useFolderBrowser";
import { logEvent } from "../../../telemetry";
import {
  formatFileSize,
  formatRelativeDate,
  getFileExtension,
  getFileIconClass,
  getFileIconKind,
  getFileIconLabel,
  getTagStyle,
} from "./fileRowMeta";
import styles from "./FolderBrowser.module.css";

function buildFolderMeta(item: GraphDriveItem): string {
  const count = item.folder?.childCount ?? 0;
  const noun = count === 1 ? "item" : "items";
  const modified = formatRelativeDate(item.lastModifiedDateTime);
  return modified ? `${count} ${noun} · ${modified}` : `${count} ${noun}`;
}

function buildFileMeta(item: GraphDriveItem): string {
  return [formatFileSize(item.size), formatRelativeDate(item.lastModifiedDateTime)]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Turn a SharePoint/OneDrive item webUrl into a "view in the browser" URL.
 * SharePoint & OneDrive honour `?web=1`, which renders the file in Office
 * Online / the built-in PDF viewer instead of streaming it as a download — so
 * clicking a file opens it in a new tab rather than downloading it (or being
 * blocked by a download-scanning proxy). Falls back to the raw URL if it can't
 * be parsed.
 *
 * Note: this opens the file in its default (editable, if permitted) surface —
 * used by the general Files/OneDrive/SharePoint browsers. Callers that need a
 * read-only view for every file type (e.g. the Knowledge Base, AIVA-7093) pass
 * `onFileClick` instead and open a Graph preview URL.
 */
function toBrowserViewUrl(webUrl: string): string {
  try {
    const url = new URL(webUrl);
    url.searchParams.set("web", "1");
    return url.toString();
  } catch {
    return webUrl;
  }
}

interface FolderBrowserProps {
  readonly items: GraphDriveItem[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly path: BreadcrumbEntry[];
  readonly emptyMessage?: ReactNode;
  readonly onBreadcrumbClick: (index: number) => void;
  readonly onFolderClick: (item: GraphDriveItem) => void;
  readonly extraCrumbs?: readonly { label: string; onClick: () => void }[];
  /**
   * Overrides how a file row opens. When supplied, clicking a file calls this
   * instead of opening the item's `webUrl` in a new tab — used by the Knowledge
   * Base to open a read-only Graph preview for every file type (AIVA-7093).
   */
  readonly onFileClick?: (item: GraphDriveItem) => void;
}

export function FolderBrowser({
  items,
  loading,
  error,
  path,
  emptyMessage,
  onBreadcrumbClick,
  onFolderClick,
  extraCrumbs,
  onFileClick,
}: Readonly<FolderBrowserProps>) {
  const folders = items.filter((it) => it.folder);
  const files = items.filter((it) => it.file);
  const isEmpty = !loading && !error && folders.length === 0 && files.length === 0;

  return (
    <div className={styles.wrap}>
      <div className={styles.crumbs} aria-label="Folder breadcrumb">
        {extraCrumbs?.map((c) => (
          <Fragment key={`extra-${c.label}`}>
            <button type="button" className={styles.crumb} onClick={c.onClick}>
              {c.label}
            </button>
            <span className={styles.sep}>›</span>
          </Fragment>
        ))}
        {path.map((entry, i) => {
          const isCurrent = i === path.length - 1;
          return (
            <Fragment key={entry.id ?? "root"}>
              <button
                type="button"
                className={`${styles.crumb} ${isCurrent ? styles.crumbCurrent : ""}`}
                onClick={() => !isCurrent && onBreadcrumbClick(i)}
                disabled={isCurrent}
              >
                {entry.name}
              </button>
              {!isCurrent && <span className={styles.sep}>›</span>}
            </Fragment>
          );
        })}
      </div>

      <div className={styles.list}>
        {error && <div className={styles.error}>{error}</div>}
        {loading && <div className={styles.empty}>Loading…</div>}
        {isEmpty && (
          <div className={styles.empty}>{emptyMessage ?? "This folder is empty."}</div>
        )}

        {folders.map((f) => (
          <button
            key={f.id}
            type="button"
            className={styles.row}
            onClick={() => onFolderClick(f)}
          >
            <div className={`${styles.icon} ${styles.iconFolder}`}>📁</div>
            <div className={styles.body}>
              <div className={styles.name}>{f.name || "Folder"}</div>
              <div className={styles.meta}>{buildFolderMeta(f)}</div>
            </div>
            <span className={styles.chev} aria-hidden="true">›</span>
          </button>
        ))}

        {files.map((f) => {
          const name = f.name || "Untitled";
          const ext = getFileExtension(name);
          const kind = getFileIconKind(ext);
          const label = getFileIconLabel(ext);
          const onClick = () => {
            if (onFileClick) {
              onFileClick(f);
              return;
            }
            if (f.webUrl) {
              logEvent("connector.download", "usage", {
                connectorSystemName: "onedrive",
                fileType: ext || "unknown",
              });
              window.open(toBrowserViewUrl(f.webUrl), "_blank", "noopener,noreferrer");
            }
          };
          return (
            <button
              key={f.id}
              type="button"
              className={styles.row}
              onClick={onClick}
            >
              <div className={`${styles.icon} ${getFileIconClass(kind)}`}>{label}</div>
              <div className={styles.body}>
                <div className={styles.name}>{name}</div>
                <div className={styles.meta}>{buildFileMeta(f)}</div>
              </div>
              {f.tags && f.tags.length > 0 && (
                <div className={styles.rowTags}>
                  {f.tags.map((t) => (
                    <span key={t} className={styles.tagChip} style={getTagStyle(t)}>{t}</span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
